import React from 'react';

interface ZoroConfigProps {
  config: {
    stockLevel?: number;
    batchSize?: number;
    requestsPerSecond?: number;
    updateFlagValue?: number;
    availabilityMap?: {
      outofstock: number;
      instock: number;
      dropship: number;
      limitedstock: number;
      backorder: number;
    };
    maxDropshipLeadtime?: number;
    storeLeadtimeDays?: number; // Store Lead Time - Tempo de manuseio da loja
    freightCostThreshold?: number;
    minimumStockThreshold?: number;
    enableMinimumStockCheck?: boolean;
    minimumPriceThreshold?: number;
    enableMinimumPriceCheck?: boolean;
    priceMarkupPercentage?: number; // Phase 2.1 - Markup % para cálculo de preço
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAvailabilityMapChange?: (field: string, value: number) => void;
}

/**
 * Componente de configuração específico para o fornecedor Zoro
 * 
 * CARACTERÍSTICAS ÚNICAS DO ZORO:
 * - Não usa API (lê direto do banco zoro_products)
 * - Availability Map configurável (mapeia availability → quantity)
 * - Regras de negócio com prioridades
 * - Freight cost threshold
 */
const ZoroConfig: React.FC<ZoroConfigProps> = ({ config, onChange, onAvailabilityMapChange }) => {
  const handleAvailabilityChange = (field: string, value: string) => {
    const numValue = parseInt(value) || 0;
    if (onAvailabilityMapChange) {
      onAvailabilityMapChange(field, numValue);
    }
  };

  const availabilityMap = config.availabilityMap || {
    outofstock: 0,
    instock: 1,
    dropship: 0,
    limitedstock: 0,
    backorder: 0
  };

  return (
    <div className="space-y-6">
      {/* Seção: Availability Map (ÚNICO DO ZORO) */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          📊 Availability Map (Configurável)
        </h3>
        <p className="text-sm text-blue-700 mb-4">
          Mapeamento de availability → quantity. Define quantos produtos enviar para Amazon baseado no status do Zoro.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Out of Stock
            </label>
            <input
              type="number"
              min="0"
              value={availabilityMap.outofstock}
              onChange={(e) => handleAvailabilityChange('outofstock', e.target.value)}
              className="input w-full"
            />
            <p className="text-xs text-gray-500 mt-1">Geralmente: 0</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              In Stock
            </label>
            <input
              type="number"
              min="0"
              value={availabilityMap.instock}
              onChange={(e) => handleAvailabilityChange('instock', e.target.value)}
              className="input w-full"
            />
            <p className="text-xs text-gray-500 mt-1">Recomendado: 1-3</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dropship
            </label>
            <input
              type="number"
              min="0"
              value={availabilityMap.dropship}
              onChange={(e) => handleAvailabilityChange('dropship', e.target.value)}
              className="input w-full"
            />
            <p className="text-xs text-gray-500 mt-1">Recomendado: 0-1</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Limited Stock
            </label>
            <input
              type="number"
              min="0"
              value={availabilityMap.limitedstock}
              onChange={(e) => handleAvailabilityChange('limitedstock', e.target.value)}
              className="input w-full"
            />
            <p className="text-xs text-gray-500 mt-1">Recomendado: 0-1</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Backorder
            </label>
            <input
              type="number"
              min="0"
              value={availabilityMap.backorder}
              onChange={(e) => handleAvailabilityChange('backorder', e.target.value)}
              className="input w-full"
            />
            <p className="text-xs text-gray-500 mt-1">Geralmente: 0</p>
          </div>
        </div>
      </div>

      {/* Seção: Handling Time */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-green-900 mb-3">
          ⏱️ Store Lead Time
        </h3>
        
        <div>
          <label htmlFor="storeLeadtimeDays" className="block text-sm font-medium text-gray-700 mb-1">
            Store Lead Time (dias)
          </label>
          <input
            id="storeLeadtimeDays"
            type="number"
            name="storeLeadtimeDays"
            min="0"
            value={config.storeLeadtimeDays || 2}
            onChange={onChange}
            className="input"
          />
          <p className="text-sm text-gray-500 mt-1">
            Tempo de manuseio da loja. Será somado ao lead time do Zoro (calculado automaticamente) para obter o handling_time_amz final.
          </p>
        </div>
        <p className="text-sm text-green-700 mt-2">
          <strong>ℹ️ Nota:</strong> O handling time do Zoro é calculado automaticamente baseado no lead time de cada produto.
        </p>
      </div>

      {/* Seção: Regras de Negócio */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-yellow-900 mb-3">
          ⚙️ Regras de Negócio
        </h3>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="minimumStockThreshold" className="block text-sm font-medium text-gray-700 mb-1">
              Estoque Mínimo (unidades)
            </label>
            <input
              id="minimumStockThreshold"
              type="number"
              name="minimumStockThreshold"
              min="0"
              value={config.minimumStockThreshold || 0}
              onChange={onChange}
              className="input"
            />
            <p className="text-sm text-gray-500 mt-1">
              Produtos com quantidade abaixo deste valor serão marcados como "Out of Stock".
            </p>
          </div>

          <div>
            <label htmlFor="enableMinimumStockCheck" className="flex items-center space-x-2">
              <input
                id="enableMinimumStockCheck"
                type="checkbox"
                name="enableMinimumStockCheck"
                checked={config.enableMinimumStockCheck || false}
                onChange={onChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Ativar verificação de estoque mínimo</span>
            </label>
          </div>

          <div>
            <label htmlFor="minimumPriceThreshold" className="block text-sm font-medium text-gray-700 mb-1">
              Preço Mínimo ($)
            </label>
            <input
              id="minimumPriceThreshold"
              type="number"
              name="minimumPriceThreshold"
              min="0"
              step="0.01"
              value={config.minimumPriceThreshold || 0}
              onChange={onChange}
              className="input"
            />
            <p className="text-sm text-gray-500 mt-1">
              Produtos com preço abaixo deste valor serão marcados como "Out of Stock".
            </p>
          </div>

          <div>
            <label htmlFor="enableMinimumPriceCheck" className="flex items-center space-x-2">
              <input
                id="enableMinimumPriceCheck"
                type="checkbox"
                name="enableMinimumPriceCheck"
                checked={config.enableMinimumPriceCheck || false}
                onChange={onChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Ativar verificação de preço mínimo</span>
            </label>
          </div>
        </div>
      </div>

      {/* Seção: Phase 2.1 - Price Markup */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-purple-900 mb-3">
          💰 Phase 2.1 - Price Markup
        </h3>
        
        <div>
          <label htmlFor="priceMarkupPercentage" className="block text-sm font-medium text-gray-700 mb-1">
            Markup Percentage (%)
          </label>
          <input
            id="priceMarkupPercentage"
            type="number"
            name="priceMarkupPercentage"
            min="0"
            step="0.1"
            value={config.priceMarkupPercentage || 15}
            onChange={onChange}
            className="input"
          />
          <p className="text-sm text-gray-500 mt-1">
            Percentual de markup aplicado ao calcular o preço Amazon.
          </p>
          <p className="text-sm text-purple-700 mt-2">
            <strong>ℹ️ Fórmula:</strong> Amazon Price = (supplier_price + shipping) × (1 + markup% / 100)
          </p>
          <p className="text-sm text-purple-700 mt-1">
            <strong>📝 Exemplo:</strong> Produto $10 + Shipping $5 com 15% markup = $17.25
          </p>
        </div>
      </div>

      {/* Seção: Dropship Lead Time */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-yellow-900 mb-3">
          📦 Dropship Configuration
        </h3>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="maxDropshipLeadtime" className="block text-sm font-medium text-gray-700 mb-1">
              Max Dropship Lead Time (dias)
            </label>
            <input
              id="maxDropshipLeadtime"
              type="number"
              name="maxDropshipLeadtime"
              min="0"
              value={config.maxDropshipLeadtime || 8}
              onChange={onChange}
              className="input"
            />
            <p className="text-sm text-gray-500 mt-1">
              Produtos dropship com lead time maior que este valor serão marcados como OUT OF STOCK.
            </p>
          </div>

          <div>
            <label htmlFor="freightCostThreshold" className="block text-sm font-medium text-gray-700 mb-1">
              Freight Cost Threshold
            </label>
            <input
              id="freightCostThreshold"
              type="number"
              name="freightCostThreshold"
              min="0"
              step="0.01"
              value={config.freightCostThreshold || 0}
              onChange={onChange}
              className="input"
            />
            <p className="text-sm text-gray-500 mt-1">
              Produtos com freight cost maior que este valor serão marcados como OUT OF STOCK. (Geralmente 0)
            </p>
          </div>
        </div>
      </div>

      {/* Seção: Configurações Gerais */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          🔧 Configurações Gerais
        </h3>

        <div>
          <label htmlFor="batchSize" className="block text-sm font-medium text-gray-700 mb-1">
            Batch Size (Lote de Processamento)
          </label>
          <input
            id="batchSize"
            type="number"
            name="batchSize"
            min="100"
            max="5000"
            value={config.batchSize || 2000}
            onChange={onChange}
            className="input"
          />
          <p className="text-sm text-gray-500 mt-1">
            Número de produtos processados por lote na Phase 1. Recomendado: 2000
          </p>
        </div>

        <div>
          <label htmlFor="updateFlagValue" className="block text-sm font-medium text-gray-700 mb-1">
            Update Flag Value
          </label>
          <input
            id="updateFlagValue"
            type="number"
            name="updateFlagValue"
            min="1"
            value={config.updateFlagValue || 7}
            onChange={onChange}
            className="input"
          />
          <p className="text-sm text-gray-500 mt-1">
            Valor do flag de atualização para produtos modificados. Padrão: 7 (STORE_ATUALIZADO)
          </p>
        </div>
      </div>

      {/* Informações Importantes */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          ℹ️ Informações Importantes
        </h4>
        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>Zoro NÃO usa API - lê direto do banco zoro_products</li>
          <li>Regras de negócio aplicadas em ordem de prioridade</li>
          <li>Freight Cost &gt; 0 sempre força OUT OF STOCK (prioridade máxima)</li>
          <li>Preço = 0 também força OUT OF STOCK</li>
          <li>Availability Map só é usado se não houver regras de prioridade</li>
        </ul>
      </div>
    </div>
  );
};

export default ZoroConfig;
